<script lang="ts">
    import { onMount, setContext, tick } from 'svelte'
    import { writable, type Writable } from 'svelte/store'

    import type { CrumbsWritableStore } from 'hydria/components/breadcrumb'
    import { cardHeaderStyle } from 'hydria/context'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { session, simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { b64dataToBytes, bytesToB64data } from 'hydria/utils/byte_utils'

    import { Breadcrumb } from 'breadcrumb'

    import Security from './Security.svelte'

    let status: 'error' | 'loading' | 'ready' = $state('loading')
    let error: string | undefined = $state(undefined)
    let types: ('sms' | 'email' | 'totp' | 'passkey')[] = $state([])
    let cType: 'sms' | 'email' | 'totp' | 'password' | 'passkey' | undefined = $state(undefined)

    let validating: boolean = $state(false)
    let code: string = $state('')
    let invalidCode: string | undefined = $state(undefined)

    let passkeyRefId: undefined | string = undefined
    let passkeyAuth: undefined | any = undefined

    let token: Writable<string | undefined> = writable(undefined)
    setContext('securityToken', token)

    let focusElement: HTMLInputElement | undefined = $state(undefined)

    // let configurationAllowed: boolean = false

    function delayFocus() {
        tick().then(() => {
            if (focusElement) {
                focusElement.focus()
                focusElement.select()
            }
        })
    }
    $effect(() => {
        if (!$token && focusElement && !validating) delayFocus()
    })

    function tokenHasExpired() {
        status = 'loading'
        error = undefined
        types = []
        cType = undefined
        code = ''
        invalidCode = undefined
        validating = false
        notify({
            type: 'warning',
            title: $_('Securité'),
            text: $_('Le jeton de sécurité a expiré. Vous devez vous reconnecter afin d’accéder à cette section.'),
        })
        if ($token == 'admin' || $token == 'recovery') {
            // Probably someone toying with bypassing the security by editing the cache in localStorage
            session.clearSpecialSignin()
        }
        $token = undefined
        getTypes()
    }

    async function getTypes() {
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', '/json/security/1/types')
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            error = r && r.data && r.data.error ? r.data.error : 'Undefined error'
            status = 'error'
        } else {
            types = r.data.types
            if (r.data.pk_auth && window.PublicKeyCredential) {
                types.push('passkey')

                passkeyRefId = r.data.pk_auth['ref_id']
                passkeyAuth = r.data.pk_auth['auth']
                passkeyAuth.challenge = await b64dataToBytes(passkeyAuth.challenge)
                for (const cred of passkeyAuth.allowCredentials) {
                    cred.id = await b64dataToBytes(cred.id)
                }
            }

            // configurationAllowed = r.data.security_configuration_allowed && (session.isSpecialSignin == null || session.isSpecialSignin == 'recovery')

            status = 'ready'

            if (session.isSpecialSignin == 'recovery' || session.isSpecialSignin == 'admin') {
                $token = session.isSpecialSignin
                status = 'ready'
            } else if (passkeyRefId) {
                selectType('passkey')
            } else if (types.length == 0) {
                selectType('password')
            } else if (types.length == 1) {
                selectType(types[0])
            } else if (types.includes('totp')) {
                selectType('totp')
            }
        }
    }
    async function fetchNewPasskeyChallenge() {
        let r: SimpleFetchResult
        status = 'loading'
        try {
            r = await simpleFetch('GET', '/json/security/1/passkey-challenge')
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            error = r && r.data && r.data.error ? r.data.error : 'Undefined error'
            status = 'error'
        } else {
            if (r.data.pk_auth && window.PublicKeyCredential) {
                passkeyRefId = r.data.pk_auth['ref_id']
                passkeyAuth = r.data.pk_auth['auth']
                passkeyAuth.challenge = await b64dataToBytes(passkeyAuth.challenge)
                for (const cred of passkeyAuth.allowCredentials) {
                    cred.id = await b64dataToBytes(cred.id)
                }
            }

            status = 'ready'
            doPasskey()
        }
    }

    async function doPasskey() {
        let credentials
        try {
            credentials = await navigator.credentials.get({ publicKey: passkeyAuth })
        } catch (e) {
            if (e instanceof DOMException) {
                console.warn('Public Key Credentials failed:', e.name)
            } else {
                console.error('Public Key Error', e)
            }
            return
        }

        if (credentials) {
            verifyPasskey(credentials)
        }
    }

    async function selectType(type: 'sms' | 'email' | 'totp' | 'password' | 'passkey') {
        cType = undefined
        if (type == 'sms' || type == 'email') {
            await sendCode(type)
        } else if (type == 'passkey') {
            cType = 'passkey'
            doPasskey()
        } else {
            cType = type
        }
        code = ''
        // delayFocus()
    }

    async function sendCode(type: 'sms' | 'email') {
        let r: SimpleFetchResult
        validating = true
        try {
            r = await simpleFetch('POST', `/json/security/1/s-token/${type}`, {})
            // r = await simpleFetch('POST', `/json/security/1/${type}`, { resend: true })
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            error = r && r.data && r.data.error ? r.data.error : 'Undefined error'
            status = 'error'
        } else {
            status = 'ready'
            cType = type
        }
        code = ''
        validating = false
        // delayFocus()
    }

    async function validateCode(code: string) {
        let r: SimpleFetchResult
        let data = {
            type: cType,
        }
        if (cType == 'password') data['password'] = code
        else data['code'] = code

        validating = true
        invalidCode = undefined

        try {
            r = await simpleFetch('POST', '/json/security/1/s-token', data)
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || (r.status != 200 && r.status != 401)) {
            error = r && r.data && r.data.error ? r.data.error : 'Undefined error'
            status = 'error'
            validating = false
            return
        }

        if (r.status == 401) {
            invalidCode = code
            delayFocus()
        } else if (r.data.success) {
            $token = r.data.token
            invalidCode = undefined
        }
        validating = false
    }

    async function verifyPasskey(credentials: any) {
        validating = true
        const ocreds: object = {
            rawId: await bytesToB64data(credentials.rawId),
            response: {} as object,
        }
        for (const key of ['id', 'type', 'authenticatorAttachment', 'clientExtensionResults']) {
            ocreds[key] = credentials[key]
        }
        for (const key of ['authenticatorData', 'clientDataJSON', 'signature', 'userHandle']) {
            ocreds['response'][key] = await bytesToB64data(credentials['response'][key])
        }

        let r: SimpleFetchResult
        let data = {
            type: cType,
            ref_id: passkeyRefId,
            pk_data: ocreds,
        }
        try {
            r = await simpleFetch('POST', '/json/security/1/s-token', data)
        } catch (e: any) {
            r = e
        }

        if (r && r.data && !r.data.success) {
            if (r.data.ecode === 'expired') {
                error = r.data.error
                status = 'error'
                validating = false
                fetchNewPasskeyChallenge()
                return
            }

            error = r && r.data && r.data.error ? r.data.error : 'Undefined error'
            status = 'error'
            validating = false
            return
        }

        if (!r || !r.data || (r.status != 200 && r.status != 401)) {
            error = r && r.data && r.data.error ? r.data.error : 'Undefined error'
            status = 'error'
            validating = false
            return
        }

        if (r.status == 401) {
            invalidCode = code
            delayFocus()
        } else if (r.data.success) {
            $token = r.data.token
            invalidCode = undefined
        }
        validating = false
    }

    onMount(() => {
        getTypes()
    })

    const crumbs: CrumbsWritableStore = writable([
        {
            txt: tgettext('User'),
        },
        {
            txt: tgettext('Sécurité'),
        },
    ])
</script>

<style>
    .twofa-types {
        display: flex;
        margin: 1em auto;
        justify-content: center;
        gap: 1em 2em;
    }
    .form-control {
        max-width: 20em;
    }
</style>

<Breadcrumb {crumbs} />

{#if session.isSpecialSignin && !['admin', 'recovery'].includes(session.isSpecialSignin)}
    <div class="card mb-4">
        <div class={cardHeaderStyle}>
            <span>{$_('Sécurité')}</span>
        </div>

        <div class="card-body">
            <p>Situation anormale à la connexion. Cette section n’est pas disponible.</p>
        </div>
    </div>
{:else if status == 'ready' && $token}
    {#if $token == 'recovery' || $token == 'admin'}
        <div class="card mb-4">
            <div class="card-body text-danger">
                {#if $token == 'admin'}
                    <p>Vous êtes connecté via un jeton administratif. Cette section est en lecture seule.</p>
                {:else}
                    <p>{$_('Vous êtes connecté à l’aide d’un code de récupération à usage unique.')}</p>
                {/if}
            </div>
        </div>
    {/if}
    <Security tokenExpired={() => tokenHasExpired()} />
{:else}
    <div class="card mb-4">
        <div class={cardHeaderStyle}>
            <span>{$_('Sécurité')}</span>
        </div>

        {#if status == 'loading' || validating}
            <div class="card-body text-info d-flex justify-content-center">
                <div class="spinner-border spinner-border" role="status" aria-hidden="true"></div>
            </div>
        {:else if status == 'error'}
            <div class="card-body text-danger">
                <p>{error}</p>
            </div>
        {:else if cType == 'password'}
            <div class="card-body">
                <form
                    onsubmit={(e) => {
                        e.preventDefault()
                        validateCode(code)
                    }}
                >
                    <p>{$_('Entrez votre mot de passe :')}</p>
                    <p><input type="password" class="form-control" disabled={validating} bind:value={code} bind:this={focusElement} /></p>
                    {#if invalidCode === code}
                        <p class="text-danger">{$_('Le mot de passe est incorrect.')}</p>
                    {/if}
                    <p><button type="submit" class="btn btn-primary" disabled={validating}>{$_('Valider')}</button></p>
                </form>
            </div>
        {:else if cType}
            {#if cType == 'passkey'}
                <div class="card-body">
                    <p>{$_('Utilisez votre application pour valider votre clef d’accès (« Passkey »).')}</p>
                </div>
            {:else if cType == 'totp'}
                <div class="card-body">
                    <p>{$_('Entrez le code généré par votre application d’authentification :')}</p>
                    <p>
                        <input
                            type="text"
                            class="form-control"
                            disabled={validating}
                            bind:value={code}
                            autocomplete="one-time-code"
                            bind:this={focusElement}
                            oninput={(v) => {
                                if (code.length == 6 && validating === false && code.match(/^[0-9]{6}$/)) {
                                    validateCode(code)
                                }
                            }}
                        />
                    </p>
                    {#if invalidCode === code}
                        <p class="text-danger">{$_('Le code est incorrect.')}</p>
                    {/if}
                </div>
            {:else if cType == 'sms'}
                <div class="card-body">
                    <p>{$_('Entrez le code reçu sur votre cellulaire :')}</p>
                    <p>
                        <input
                            type="text"
                            class="form-control"
                            disabled={validating}
                            bind:value={code}
                            bind:this={focusElement}
                            autocomplete="one-time-code"
                            oninput={(v) => {
                                if (code.length == 6 && validating === false && code.match(/^[0-9]{6}$/)) {
                                    validateCode(code)
                                }
                            }}
                        />
                    </p>
                    {#if invalidCode === code}
                        <p class="text-danger">{$_('Le code est incorrect.')}</p>
                    {/if}
                </div>
            {:else if cType == 'email'}
                <div class="card-body">
                    <p>{$_('Entrez le code reçu sur votre adresse courriel :')}</p>
                    <p>
                        <input
                            type="text"
                            class="form-control"
                            disabled={validating}
                            bind:value={code}
                            bind:this={focusElement}
                            autocomplete="one-time-code"
                            oninput={(v) => {
                                if (code.length == 6 && validating === false && code.match(/^[0-9]{6}$/)) {
                                    validateCode(code)
                                }
                            }}
                        />
                    </p>
                    {#if invalidCode === code}
                        <p class="text-danger">{$_('Le code est incorrect.')}</p>
                    {/if}
                </div>
            {/if}
            <div class="switch">
                <div class="card-footer text-bg-light d-flex justify-content-end">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        disabled={validating}
                        onclick={() => {
                            cType = undefined
                        }}>{$_('Utiliser une autre méthode')}</button
                    >
                </div>
            </div>
        {:else}
            <div class="card-body">
                <p>{$_('Veuillez choisir une méthode de vérification.')}</p>
                <div class="twofa-types">
                    {#if types.includes('passkey')}
                        <div><button class="btn btn-outline-primary" type="button" onclick={() => selectType('passkey')} disabled={validating}>{$_('Clé d’accès (« Passkey »)')}</button></div>
                    {/if}
                    {#if types.includes('totp')}
                        <div><button class="btn btn-outline-primary" type="button" onclick={() => selectType('totp')} disabled={validating}>{$_('Application d’authentification')}</button></div>
                    {/if}
                    {#if types.includes('sms')}
                        <div><button class="btn btn-outline-primary" type="button" onclick={() => selectType('sms')} disabled={validating}>{$_('SMS / Message texte')}</button></div>
                    {/if}
                    {#if types.includes('email')}
                        <div><button class="btn btn-outline-primary" type="button" onclick={() => selectType('email')} disabled={validating}>{$_('Courriel')}</button></div>
                    {/if}
                </div>
            </div>
        {/if}
    </div>
{/if}
